// src/redux/services/store/storeCartApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

/** ---------------- Types (MATCH YOUR BACKEND) ---------------- */

export type StoreCartProductImage = {
  cdn_url?: string | null;
  alt_text?: string | null;
};

export type StoreCartItemProduct = {
  product_id: number;
  name: string;
  slug: string;
  product_type?: "simple" | "variable";

  // backend includes variant info here
  variant_id: number;
  sku?: string | null;
  attributes_json?: Record<string, any> | null;

  image?: StoreCartProductImage | null;

  in_stock?: boolean;
  currency_code?: string | null;
};

export type StoreCartItemOut = {
  id: number;
  qty: string; // numeric -> string from backend
  unit_price: string;
  compare_at_price?: string | null;
  discount_amount: string;
  discount_percent?: string | null;
  tax_amount: string;
  line_total: string;
  customization_enabled?: boolean;
  customization_color?: string | null;
  customization_note?: string | null;
  product: StoreCartItemProduct;
};

export type StoreCartTotals = {
  subtotal: string;
  discount: string;
  tax: string;
  total: string;
};

export type StoreCartResponse = {
  cart: {
    id: number;
    status: "active" | "converted" | "abandoned";
    currency_code: string;
  };
  items: StoreCartItemOut[];
  totals: StoreCartTotals;
};

export type StoreCartRemoveResponse = {
  ok: boolean;
};

/** ---------------- API ---------------- */

type Builder = EndpointBuilder<any, any, any>;

export const storeCartApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    // GET current cart
    getStoreCart: builder.query<StoreCartResponse, void>({
      query: () => ({
        url: `/store/cart`,
        method: "GET",
      }),
      providesTags: () => [{ type: "StoreCart", id: "CART" }],
    }),

    // POST add item
    addToCart: builder.mutation<
      StoreCartResponse,
      {
        variant_id: number;
        qty?: number | string;
        customization_enabled?: boolean;
        customization_color?: string | null;
        customization_note?: string | null;
      }
    >({
      query: (body) => ({
        url: `/store/cart/items`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: "StoreCart", id: "CART" }],
    }),

    // ✅ PATCH update qty (MATCH BACKEND)
    updateCartItemQty: builder.mutation<StoreCartResponse, { id: number; qty: number | string }>({
      query: ({ id, qty }) => ({
        url: `/store/cart/items/${id}`,
        method: "PATCH", // ✅ was PUT
        body: { qty },   // { qty: 2 } or { qty: "2.000" }
      }),
      invalidatesTags: () => [{ type: "StoreCart", id: "CART" }],
    }),

    // ✅ DELETE item (MATCH BACKEND RESPONSE {ok:true})
    removeCartItem: builder.mutation<StoreCartRemoveResponse, { id: number }>({
      query: ({ id }) => ({
        url: `/store/cart/items/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: () => [{ type: "StoreCart", id: "CART" }],
    }),

    // ❌ backend route not present in your posted store_cart.py
    // If you add backend endpoint later, uncomment and match it.
    // clearCart: builder.mutation<StoreCartResponse, void>({
    //   query: () => ({
    //     url: `/store/cart/clear`,
    //     method: "POST",
    //   }),
    //   invalidatesTags: () => [{ type: "StoreCart", id: "CART" }],
    // }),
  }),
  overrideExisting: false,
});

export const {
  useGetStoreCartQuery,
  useAddToCartMutation,
  useUpdateCartItemQtyMutation,
  useRemoveCartItemMutation,
  // useClearCartMutation,
} = storeCartApi;
